import React from 'react';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { isAPIError } from 'types/api_error';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthentication } from 'hooks/api/useAuthentication';
import { useOwnedRingApplications } from 'hooks/useOwnedRingApplications';
import { useRegistrationParams } from 'hooks/useRegistrationParams';

import LogoIcon from 'images/lumeo-logo-icon.svg?react';

import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import { Heading } from 'components/Heading/Heading';
import { Loader } from 'components/Loader/Loader';
import { Text } from 'components/Text/Text';

/**
 * One-way Ring linking for a user who already has an account. A new user registers at /ring/link and
 * the signup carries the nonce, but a returning user runs no signup, so nothing would ever hand the
 * nonce to the server — this does. Root routes here only for a Ring-plan account.
 */
export function RingBind() {
  const api = useAPI();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthentication();
  const queryClient = useQueryClient();
  const { ownedRingApplications, isLoading, isError, refetch } =
    useOwnedRingApplications();
  const { getParams, clearParams } = useRegistrationParams();

  const params = getParams();
  const nonce = params?.nonce;
  const time = params?.time;

  const [error, setError] = React.useState<string>();
  const [isBinding, setIsBinding] = React.useState(false);
  const hasBound = React.useRef(false);

  const bind = React.useCallback(
    (applicationId: string) => {
      if (hasBound.current || !nonce || !time) {
        return;
      }
      hasBound.current = true;
      setIsBinding(true);

      api.applications
        .ringBind(applicationId, { nonce, time: Number(time) })
        .then(async () => {
          // Binding clears any ring_unlinked suspension server-side. The org query is cached with
          // staleTime: Infinity, so drop it (and the org list) or the suspension banner lingers.
          await Promise.all([
            queryClient.invalidateQueries(['organization']),
            queryClient.invalidateQueries(['organizations']),
          ]);
          clearParams();
          navigate('/', { replace: true });
        })
        .catch((caught: unknown) => {
          captureException(caught, { extra: { applicationId } });
          const status = isAPIError(caught) ? caught.status : undefined;

          if (status === 401) {
            setError(
              'Your account is not allowed to link this workspace. Only an owner of the organization can finish linking.'
            );
          } else if (status === 403) {
            setError(
              'This workspace is not on a Ring plan, so it cannot be linked to Ring.'
            );
          } else if (status === undefined || status >= 500) {
            setError(
              'We could not finish linking your Ring account. Please try again in a few minutes.'
            );
          } else {
            setError(
              'We could not link your Ring account. The link may have expired — please restart the integration from the Ring app.'
            );
          }
        });
    },
    [api, nonce, time, queryClient, clearParams, navigate]
  );

  // Exactly one owned Ring workspace: bind it without asking.
  React.useEffect(() => {
    if (isLoading || isError || hasBound.current || !nonce || !time) {
      return;
    }
    if (ownedRingApplications.length === 1) {
      bind(ownedRingApplications[0].id);
    }
  }, [isLoading, isError, ownedRingApplications, bind, nonce, time]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  if (!nonce || !time) {
    return <Navigate to="/" />;
  }

  function handleProceed() {
    clearParams();
    navigate('/', { replace: true });
  }

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <div className="w-full max-w-md space-y-8 px-4">
          <header className="flex flex-col items-center space-y-6">
            <LogoIcon className="h-12 w-auto" />

            <Heading level="1" className="text-center">
              Could not link your Ring account
            </Heading>

            <Text size="large" className="text-center text-gray-600">
              {error}
            </Text>
          </header>

          <div className="flex justify-center pt-4">
            <Button variant="primary" size="large" onClick={handleProceed}>
              Continue to Lumeo
            </Button>
          </div>
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <div className="w-full max-w-md space-y-8 px-4">
          <header className="flex flex-col items-center space-y-6">
            <LogoIcon className="h-12 w-auto" />

            <Heading level="1" className="text-center">
              Could not load your workspaces
            </Heading>

            <Text size="large" className="text-center text-gray-600">
              We could not check which workspaces you can link. Please try again.
            </Text>
          </header>

          <div className="flex justify-center pt-4">
            <Button variant="primary" size="large" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </main>
    );
  }

  if (isLoading || isBinding || ownedRingApplications.length === 1) {
    return <Loader text="Linking your Ring account..." showLogo />;
  }

  if (ownedRingApplications.length === 0) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <div className="w-full max-w-md space-y-8 px-4">
          <header className="flex flex-col items-center space-y-6">
            <LogoIcon className="h-12 w-auto" />

            <Heading level="1" className="text-center">
              No Ring workspace to link
            </Heading>

            <Text size="large" className="text-center text-gray-600">
              We could not find a Ring workspace you own. Please restart the
              integration from the Ring app.
            </Text>
          </header>

          <div className="flex justify-center pt-4">
            <Button variant="primary" size="large" onClick={handleProceed}>
              Continue to Lumeo
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center">
      <div className="w-full max-w-md space-y-8 px-4">
        <header className="flex flex-col items-center space-y-6">
          <LogoIcon className="h-12 w-auto" />

          <Heading level="1" className="text-center">
            Choose a workspace to link
          </Heading>

          <Text size="large" className="text-center text-gray-600">
            You own more than one Ring workspace. Pick the one to link your Ring
            account to.
          </Text>
        </header>

        <div className="flex flex-col gap-3 pt-2">
          {ownedRingApplications.map((application) => (
            <Card key={application.id} onClick={() => bind(application.id)}>
              <Text size="large">{application.name}</Text>
            </Card>
          ))}
        </div>
      </div>
    </main>
  );
}
